/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { ICookieValue, IImageSettings, Image } from '@msdyn365-commerce/core';
import * as React from 'react';
import Modal from 'reactstrap/lib/Modal';
import ModalBody from 'reactstrap/lib/ModalBody';
import { IAgeGateData } from './age-gate.data';
import { IAgeGateProps } from './age-gate.props.autogenerated';

// =========================================================================
// MODULE-WIDE CONSTANTS
// =========================================================================
export const AgeGateCookieName = 'ageGateCookie';

/**
 *
 * AgeGate component
 * @extends {React.PureComponent<IAgeGateProps<IAgeGateData>>}
 */
class AgeGate extends React.PureComponent<IAgeGateProps<IAgeGateData>> {

    // ------------------------------------------------------
    // ------------------------------------------------------
    constructor(props: IAgeGateProps<IAgeGateData>) {
        super(props);
        this._setCookie = this._setCookie.bind(this);
    }

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================

    // ------------------------------------------------------
    // ------------------------------------------------------
    public render(): JSX.Element | null {
        const { config } = this.props;
        const cookie = this.props.context.request.cookies;

        // tslint:disable-next-line: no-typeof-undefined
        if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
            return null;
        }

        try {
            cookie.setConsentCookie();
        } catch (error) {
            console.log(error);
        }

        // Check for ageGateCookie to determine if user has already accepted agreement
        // Also ensure the code is running in the web context. Don't show the modal in the CMS.
        switch (this._isAge21 || process.env.CURRENT_ENVIRONMENT !== 'web' || this._isPrivacyPage) {
            case true:
                return null;
            default:
                return (
                    <Modal
                        modalClassName='age-gate-modal'
                        autoFocus={true}
                        fade={true}
                        isOpen={true}
                        centered={true}
                        zIndex={1000}
                        applicationNode={'rsg-root'}
                    >
                        <ModalBody>
                            <div className='age-gate-container'>
                                {this._newImage}
                                {this._oldImage}
                                <h4>{config.ageGateBody}</h4>
                                <h2>{config.ageGateAgreement}</h2>
                                <button className='btn btn-outline-secondary btn-lg age-gate-button' onClick={this._setCookie}>{config.ageGateBold}<span className='age-gate-button-text'>{config.ageGateCTA}</span></button>
                                <div className='age-gate-links'>
                                    {this._privacyRightsLink}
                                    {this._myInformationLink}
                                </div>
                            </div>
                        </ModalBody>
                    </Modal>
                );
        }
    }

    // =========================================================================
    // PRIVATE METHODS
    // =========================================================================

    // ------------------------------------------------------
    // The privacy page should not show the age gate if the
    // privacy link is defined in the config. This checks
    // and ensures that the current page that is about to
    // recieve an age gate popup is the privacy page
    // ------------------------------------------------------
    private get _isPrivacyPage(): boolean {
        const { privacyLink } = this.props.config;
        if (privacyLink) {
            // we dont actually care about origin, we only need to extract the
            // exact pathname and the object requires an origin
            const privacyPath =
                new URL(privacyLink.linkUrl.destinationUrl, window.location.origin).pathname;

            return privacyPath === window.location.pathname;
        }
        return false;
    }

    // ------------------------------------------------------
    // ------------------------------------------------------
    private _closeModal(): void {
        this.setState(() => ({
            isOpen: false
        }));
    }

    // ------------------------------------------------------
    // ------------------------------------------------------
    private _setCookie(): void {
        this.props.context.request.cookies.set(AgeGateCookieName, true, { path: '/', maxAge: this.props.config.cookieMaxAge });
        this._closeModal();
    }

    // ------------------------------------------------------
    // ------------------------------------------------------
    private get _isAge21(): boolean {
        const cookie = this.props.context.request.cookies;
        const ageGateCookie = cookie.get(AgeGateCookieName) as ICookieValue<{}> | undefined;

        if (ageGateCookie === undefined || ageGateCookie.value === 'false') { return false; }

        return (!!ageGateCookie.value);
    }

    // =========================================================================
    // JSX CONSTRUCTORS
    // =========================================================================

    // ------------------------------------------------------
    // ------------------------------------------------------
    private get _newImage(): JSX.Element | null {
        const image = this.props.config.ageGateImage;
        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { q: `w=24&h=24&m=6`, w: 0, h: 0 },
                lg: { q: `w=24&h=24&m=6`, w: 0, h: 0 }
            },
            lazyload: true
        };

        if (image && image.src) {
            return (
                <Image
                    className='age-gate-logo'
                    {...image}
                    gridSettings={this.props.context.request.gridSettings!}
                    imageSettings={image.imageSettings || defaultImageSettings}
                />
            );
        } else { return null; }
    }

    // ------------------------------------------------------
    // ------------------------------------------------------
    private get _oldImage(): JSX.Element | null {

        // If there's a new image, ignore any old image settings
        const newImage = this.props.config.ageGateImage;
        if (newImage && newImage.src) { return null; }

        // Use the old format: a raw string containing (hopefully) a URL
        const image = this.props.config.ageGateLogo;
        if (image) {
            return (
                <img className='age-gate-logo' src={image} alt='winery logo' />
            );
        } else { return null; }
    }

    // -----------------------------------------------------------
    // -----------------------------------------------------------
    private get _myInformationLink(): JSX.Element | null {

        const link = this.props.config.ageGateMyInformation;

        if (link && link.linkUrl.destinationUrl) {
            return (
                <a
                    href={link.linkUrl.destinationUrl}
                    aria-label={link.ariaLabel}
                    target={link.openInNewTab ? '_blank' : undefined}
                    className='age-gate-my-information-link' rel="noreferrer"
                >
                    {link.linkText}
                </a>
            );
        } else {
            return null;
        }
    }

    // -----------------------------------------------------------
    // -----------------------------------------------------------
    private get _privacyRightsLink(): JSX.Element | null {

        const link = this.props.config.ageGatePrivacyRights;

        if (link && link.linkUrl.destinationUrl) {
            return (
                <a
                    href={link.linkUrl.destinationUrl}
                    aria-label={link.ariaLabel}
                    target={link.openInNewTab ? '_blank' : undefined}
                    className='age-gate-privacy-rights-link' rel="noreferrer"
                >
                    {link.linkText}
                </a>
            );
        } else {
            return null;
        }
    }

}

export default AgeGate;
